/**
 * Canonical schema.org entity graph for soulminter.io.
 *
 * Every page emits ONE <script type="application/ld+json"> containing an
 * "@graph" built from these helpers. The point of the shared module is the
 * "@id" values below: a consumer that sees `#organization` on /about and the
 * same `#organization` on /create knows it is looking at one entity, not two
 * companies that happen to share a name. Before this file existed the
 * Organization node was hand-copied into three pages and had already drifted
 * (one carried the full street address, another only a country), which is
 * exactly the ambiguity that stops an answer engine from resolving a brand.
 *
 * House rule, and the reason several obvious-looking additions are absent:
 * *** only mark up what a human can actually see on the page. ***
 * FAQPage without a rendered FAQ, or HowTo without rendered steps, violates
 * Google's structured-data guidelines and is a manual-action risk. Where a
 * page has visible questions or steps, the schema is built from the very same
 * array the page renders, so the two cannot drift apart.
 */

export const SITE_URL = "https://soulminter.io";

/**
 * Brand assets are served from our own origin, not an IPFS gateway. The gateway
 * copy was a 1.0 MB 1777x1777 PNG behind a third-party DNS + TLS handshake, and
 * it was wired into the favicon link -- so every visitor downloaded a megabyte
 * to draw a 16px tab icon. Both URLs must stay absolute: schema.org consumers
 * and social crawlers reject relative image paths.
 *
 * Note these are two different pictures for two different jobs. SITE_LOGO is
 * the square mark an entity graph expects; SITE_OG_IMAGE is a 1200x630 card,
 * the ratio every social scraper crops to. Do not collapse them back into one.
 */
export const SITE_LOGO = "https://soulminter.io/logo-mark.png";

export const SITE_OG_IMAGE = "https://soulminter.io/og-image.png";

/**
 * The author of the blog, and the natural person operating the site. Kept here
 * rather than in lib/blog.ts because both the visible author card and the
 * Person node below read from it, and lib/blog.ts is server-only (fs).
 */
export const AUTHOR_NAME = "Mate Roth";
export const AUTHOR_BIO = "Owner and operator of soulminter.io";

/** Stable node identifiers. Never change these -- they are the entity keys. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const SOFTWARE_ID = `${SITE_URL}/#software`;
export const BLOG_ID = `${SITE_URL}/blog#blog`;
export const PERSON_ID = `${SITE_URL}/#person`;

/** Reference an already-defined node instead of repeating its properties. */
export const orgRef = { "@id": ORG_ID };
export const websiteRef = { "@id": WEBSITE_ID };

type Json = Record<string, unknown>;

/** A question/answer pair as rendered on the page. */
export interface FaqItem {
  q: string;
  a: string;
}

/** One step of a process as rendered on the page. */
export interface HowToStep {
  name: string;
  text: string;
}

/** Breadcrumb trail entry. `url` is absolute. */
export interface Crumb {
  name: string;
  url: string;
}

/**
 * The operating entity. Mirrors the details published on /about, which is the
 * page a reader (or a crawler checking who is behind the site) lands on.
 * `knowsAbout` states the topics this entity is actually authoritative on --
 * a cheap, honest signal for entity resolution in AI answers.
 */
export const organization: Json = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "SoulMinter",
  legalName: "ROTH MATE EV",
  url: SITE_URL,
  logo: {
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#logo`,
    url: SITE_LOGO,
    width: 512,
    height: 512,
    caption: "SoulMinter",
  },
  image: { "@id": `${SITE_URL}/#logo` },
  email: "main@soulminter.io",
  telephone: "+43-77-63101939",
  description:
    "Non-custodial software tool for creating SPL and Token-2022 mints on the Solana blockchain. Operated by ROTH MATE EV, a Hungarian sole proprietorship (egyeni vallalkozo).",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Gyóni Géza utca 4, 1 em. 4 a.",
    addressLocality: "Sopron",
    postalCode: "9400",
    addressCountry: "HU",
  },
  taxID: "91818627-1-28",
  founder: {
    "@type": "Person",
    "@id": PERSON_ID,
    name: AUTHOR_NAME,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "main@soulminter.io",
      telephone: "+43-77-63101939",
      availableLanguage: ["English", "Hungarian"],
    },
  ],
  knowsAbout: [
    "Solana blockchain",
    "SPL tokens",
    "Token-2022 program",
    "Token extensions",
    "Mint authority and freeze authority",
    "On-chain token metadata",
    "No-code token creation",
  ],
  sameAs: ["https://x.com/soulminter_io"],
};

/**
 * The person behind the site: the sole proprietor named on /about and the
 * author of every blog article. Shares one @id with `organization.founder`,
 * so the operator a reader meets on /about and the byline they see on an
 * article resolve to the same entity rather than two similarly named people.
 *
 * Only emitted where the name and bio are actually rendered -- currently the
 * blog, whose article pages carry a visible author card. Elsewhere the founder
 * reference above is the whole claim.
 */
export const person: Json = {
  "@type": "Person",
  "@id": PERSON_ID,
  name: AUTHOR_NAME,
  alternateName: "Máté Roth",
  url: `${SITE_URL}/about`,
  description: AUTHOR_BIO,
  jobTitle: "Owner and operator",
  worksFor: orgRef,
  knowsAbout: [
    "Solana blockchain",
    "SPL tokens",
    "Token-2022 program",
    "Token extensions",
    "No-code token creation",
  ],
};

/**
 * The site itself.
 *
 * Deliberately no `potentialAction`/SearchAction: the previous homepage markup
 * declared a search endpoint at /search?q=, and there is no /search route. A
 * SearchAction pointing at a 404 is a false claim, and false claims are the
 * fastest way to get the rest of a site's markup discounted.
 */
export const website: Json = {
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  name: "SoulMinter",
  url: SITE_URL,
  description:
    "No-code Solana token creator. Flat-fee SPL and Token-2022 mints with custom mint addresses, on-chain social metadata, and authority revocation included.",
  publisher: orgRef,
  inLanguage: "en",
};

/**
 * The product. One SoftwareApplication node for the whole tool, with an
 * AggregateOffer spanning both tiers -- the shape an answer engine needs to
 * state "SoulMinter costs 0.05-0.1 SOL flat" without guessing.
 */
export const softwareApplication: Json = {
  "@type": "SoftwareApplication",
  "@id": SOFTWARE_ID,
  name: "SoulMinter",
  url: SITE_URL,
  description:
    "Non-custodial web application for creating SPL and Token-2022 mints on Solana. Transactions are assembled in the browser and signed by the user's own wallet.",
  applicationCategory: "DeveloperApplication",
  applicationSubCategory: "Blockchain token creator",
  operatingSystem: "Web browser",
  browserRequirements:
    "Requires JavaScript and a Solana wallet implementing the Wallet Standard.",
  isAccessibleForFree: false,
  image: SITE_LOGO,
  provider: orgRef,
  publisher: orgRef,
  featureList: [
    "Create SPL and Token-2022 mints without writing code",
    "Custom vanity mint address included at no extra cost",
    "On-chain social metadata (website, X, Telegram) included",
    "Revoke mint, freeze, and update authorities at creation or later",
    "Token-2022 extensions on the Pro tier: transfer fees, non-transferable (soulbound) mode, permanent delegate, pausable mint, interest-bearing balances, opt-in memo enforcement, whitelist-only holders",
    "Non-custodial: keys never leave the user's wallet",
  ],
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "SOL",
    lowPrice: "0.05",
    highPrice: "0.1",
    offerCount: 2,
    description:
      "Two flat tiers with no per-feature charges and no percentage of supply taken.",
    offers: [
      {
        "@type": "Offer",
        name: "SoulMinter Standard",
        price: "0.05",
        priceCurrency: "SOL",
        url: `${SITE_URL}/create/standard`,
        availability: "https://schema.org/InStock",
        description:
          "Flat 0.05 SOL. Custom mint address, on-chain social metadata, Token-2022 support, and Mint/Freeze/Update authority revocation included.",
        seller: orgRef,
      },
      {
        "@type": "Offer",
        name: "SoulMinter Pro",
        price: "0.1",
        priceCurrency: "SOL",
        url: `${SITE_URL}/create/pro`,
        availability: "https://schema.org/InStock",
        description:
          "Flat 0.1 SOL. Adds Token-2022 extensions configured at mint time: transfer fees, soulbound mode, permanent delegate, pausable mint, interest-bearing balances, opt-in memo enforcement, and whitelist-only holders.",
        seller: orgRef,
      },
    ],
  },
};

/**
 * The blog as an entity.
 *
 * Emitted on the index AND on every article, because an article's
 * `isPartOf` has to resolve to a node the same document defines -- a
 * reference to an @id that only exists on another page is dangling. The
 * index adds `blogPost` on top of this; consumers merge the two by @id.
 */
export const blog: Json = {
  "@type": "Blog",
  "@id": BLOG_ID,
  name: "SoulMinter Blog",
  url: `${SITE_URL}/blog`,
  description:
    "Guides on creating Solana tokens, SPL token authorities, and Token-2022 extensions.",
  inLanguage: "en",
  isPartOf: websiteRef,
  publisher: orgRef,
};

/**
 * Author node for an article byline. A byline matching AUTHOR_NAME resolves to
 * the shared Person entity above; any other name gets a plain Person node,
 * because claiming the site operator wrote a guest post would be a false claim.
 */
export function articleAuthor(name: string): Json {
  return name === AUTHOR_NAME
    ? { "@type": "Person", "@id": PERSON_ID, name: AUTHOR_NAME }
    : { "@type": "Person", name };
}

/** BreadcrumbList node. `pageUrl` owns the fragment so the @id stays unique. */
export function breadcrumb(pageUrl: string, trail: Crumb[]): Json {
  return {
    "@type": "BreadcrumbList",
    "@id": `${pageUrl}#breadcrumb`,
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: crumb.url,
    })),
  };
}

/** Question[] for a page node's `mainEntity`. Built from the rendered array. */
export function questions(items: FaqItem[]): Json[] {
  return items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  }));
}

/**
 * Standalone HowTo node.
 *
 * Google retired HowTo rich results, so this earns nothing in the blue links.
 * It is here for the generative engines, which still parse step lists to
 * answer "how do I ..." prompts. Only call this where the steps are rendered.
 */
export function howTo(params: {
  id: string;
  name: string;
  description: string;
  steps: HowToStep[];
  pageUrl?: string;
  totalTime?: string;
}): Json {
  const node: Json = {
    "@type": "HowTo",
    "@id": params.id,
    name: params.name,
    description: params.description,
    inLanguage: "en",
    step: params.steps.map((step, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: step.name,
      text: step.text,
    })),
  };

  if (params.totalTime) {
    node.totalTime = params.totalTime;
  }
  if (params.pageUrl) {
    node.isPartOf = { "@id": `${params.pageUrl}#webpage` };
  }

  return node;
}

/**
 * The page node.
 *
 * When `faqs` are supplied the node is typed ["WebPage", "FAQPage"] rather
 * than emitting a second, competing page node for the same URL -- FAQPage is
 * a subtype of WebPage, so one URL should resolve to one page node wearing
 * both labels.
 */
export function webPage(params: {
  url: string;
  name: string;
  description: string;
  type?: string | string[];
  image?: string;
  trail?: Crumb[];
  faqs?: FaqItem[];
  datePublished?: string;
  dateModified?: string;
}): Json {
  const baseType = params.type ?? "WebPage";
  const types = Array.isArray(baseType) ? [...baseType] : [baseType];

  if (params.faqs?.length && !types.includes("FAQPage")) {
    types.push("FAQPage");
  }

  const node: Json = {
    "@type": types.length === 1 ? types[0] : types,
    "@id": `${params.url}#webpage`,
    url: params.url,
    name: params.name,
    description: params.description,
    isPartOf: websiteRef,
    about: orgRef,
    publisher: orgRef,
    inLanguage: "en",
  };

  if (params.image) {
    node.primaryImageOfPage = {
      "@type": "ImageObject",
      url: params.image,
    };
  }
  if (params.trail?.length) {
    node.breadcrumb = { "@id": `${params.url}#breadcrumb` };
  }
  if (params.faqs?.length) {
    node.mainEntity = questions(params.faqs);
  }
  if (params.datePublished) {
    node.datePublished = params.datePublished;
  }
  if (params.dateModified) {
    node.dateModified = params.dateModified;
  }

  return node;
}

/**
 * Assemble the page graph. Organization and WebSite are prepended on every
 * page on purpose: each page must stand on its own for a crawler that fetched
 * only that URL, and the shared @id is what merges the repeats into one entity.
 */
export function graph(nodes: (Json | null | undefined | false)[]): Json {
  return {
    "@context": "https://schema.org",
    "@graph": [organization, website, ...nodes.filter(Boolean)] as Json[],
  };
}

/** Serialize for dangerouslySetInnerHTML. */
export function ld(node: Json): string {
  return JSON.stringify(node);
}
